import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useApi, useLoggedIn } from "@/hooks";

const LOCAL_STORAGE_REGISTRATION_DATA = "truth:registration-data";

interface Response {
  access_token: string;
  created_at: string;
}

interface RegistrationData {
  token: string;
  timestamp: number;
}

function fetchStoredRegistrationToken(): RegistrationData | undefined {
  try {
    return JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_REGISTRATION_DATA) as string,
    );
  } catch {
    return undefined;
  }
}

function removeStoredRegistrationToken() {
  localStorage.removeItem(LOCAL_STORAGE_REGISTRATION_DATA);
}

/**
 * Check to see whether this registration session is expired, which means
 * it was created more than 48 hours ago.
 * @param date Date
 * @returns boolean
 */
function isExpired(timestamp: number): boolean {
  const date = new Date(timestamp);
  const currentTimeInMilliseconds = Date.now();
  const dateInMilliseconds = Number(date);

  // Get the difference between the given date and the current time in hours
  const hoursDifference =
    (currentTimeInMilliseconds - dateInMilliseconds) / (1000 * 60 * 60);

  // Compare the difference with 48 hours
  if (hoursDifference > 48) {
    return true;
  } else {
    return false;
  }
}

interface UseRegistrationTokenOptions {
  isEnabled?: boolean;
}

/**
 * Fetch the registration data from Pepe and cache the results in localStorage.
 * - If we have valid data from localStorage, let's use that.
 * - If the localStorage data has expired, force a refresh.
 */
function useRegistrationToken({
  isEnabled,
}: UseRegistrationTokenOptions = {}) {
  const api = useApi();
  const { isLoggedIn } = useLoggedIn();
  const shouldFetch = isEnabled ?? !isLoggedIn;

  const queryInfo = useQuery<RegistrationData>({
    queryKey: ["registrations", "token"],
    cacheTime: 172800000, // 48 hours
    staleTime: Infinity,
    enabled: shouldFetch,
    queryFn: async () => {
      const { data } = await api.post<Response>("/api/v1/pepe/registrations", {
        flow: "v2",
      });
      return {
        token: data.access_token,
        timestamp: Date.now(),
      };
    },
    initialData() {
      const currentRegistrationData = fetchStoredRegistrationToken();
      if (
        currentRegistrationData &&
        isExpired(currentRegistrationData.timestamp)
      ) {
        removeStoredRegistrationToken();
        return undefined;
      }

      if (currentRegistrationData) {
        return currentRegistrationData;
      }
    },
  });

  useEffect(() => {
    if (queryInfo.status === "success") {
      localStorage.setItem(
        LOCAL_STORAGE_REGISTRATION_DATA,
        JSON.stringify(queryInfo.data),
      );
    }
  }, [queryInfo.status]);

  return {
    ...queryInfo,
    isInitialLoading: shouldFetch && queryInfo.isInitialLoading,
    token: queryInfo.data?.token!,
  };
}

export { useRegistrationToken, LOCAL_STORAGE_REGISTRATION_DATA };
